/**
 * CPA-ONBOARDING-1 — the identity half of the firm profile form.
 *
 * Legal name, licensed CPA, license no/state, engagement code prefix. These are
 * the fields the backend requires before draft -> active.
 */
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import type { FormState, UpdateField } from './model'

interface Props {
  form: FormState
  update: UpdateField
  licenseStates: string[]
}

export function FirmIdentityCard({ form, update, licenseStates }: Props) {
  const prefixPreview = form.engagement_code_prefix.trim().toUpperCase() || 'ACM'

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle>Firm Identity</CardTitle>
        <CardDescription>Required fields are marked *.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="firm_legal_name">Firm legal name *</Label>
          <Input
            id="firm_legal_name"
            value={form.firm_legal_name}
            onChange={e => update('firm_legal_name', e.target.value)}
            placeholder="Acme CPA LLP"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="firm_dba">Doing business as</Label>
            <Input
              id="firm_dba"
              value={form.firm_dba}
              onChange={e => update('firm_dba', e.target.value)}
              placeholder="Acme Advisors"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="firm_website">Website</Label>
            <Input
              id="firm_website"
              value={form.firm_website}
              onChange={e => update('firm_website', e.target.value)}
              placeholder="https://example-cpa.com"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="cpa_name">Licensed CPA *</Label>
          <Input
            id="cpa_name"
            value={form.cpa_name}
            onChange={e => update('cpa_name', e.target.value)}
            placeholder="Jordan Reyes, CPA"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="cpa_license_no">License number *</Label>
            <Input
              id="cpa_license_no"
              value={form.cpa_license_no}
              onChange={e => update('cpa_license_no', e.target.value)}
              placeholder="102938"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cpa_license_state">License state *</Label>
            <Select
              value={form.cpa_license_state || undefined}
              onValueChange={val => update('cpa_license_state', val ?? '')}
            >
              <SelectTrigger id="cpa_license_state">
                <SelectValue placeholder="Select state" />
              </SelectTrigger>
              <SelectContent>
                {licenseStates.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="engagement_code_prefix">Engagement code prefix *</Label>
          <Input
            id="engagement_code_prefix"
            value={form.engagement_code_prefix}
            onChange={e => update('engagement_code_prefix', e.target.value.toUpperCase())}
            placeholder="ACM"
            maxLength={6}
          />
          <p className="text-xs text-muted-foreground">
            2–6 letters/digits, starting with a letter. Engagements read{' '}
            <code>{prefixPreview}-0001</code>. Must be unique across firms.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
